import {
  ChangeDetectorRef,
  Input,
  Component,
  OnDestroy,
  OnInit,
} from '@angular/core';
import {
  GroupedBarChartDataModel,
  Sort,
  SortDirection,
  TooltipOptions,
  Utils,
} from 'marketpro-design-lib';
import { regionsMap } from 'projects/marketpro-ui/src/app/regions';
import { combineLatest, Subject } from 'rxjs';
import { debounceTime, takeUntil, tap } from 'rxjs/operators';
import { constants } from '../../../../constants';
import { AuthService } from '../../../auth/services/auth.service';
import { AuthorizedResourcesService } from '../../../auth/services/authorized-resources.service';
import {
  GroupedResponse,
  GroupedResponseTwoLevels,
} from '../../models/grouped-statistics-response.model';
import { SimpleStatisticsResponse } from '../../models/simple-statistics-response.model';
import { TableRegion } from '../../models/table-region.model';
import {
  DataFilters,
  DataFiltersService,
} from '../../services/data-filters.service';
import { DataService } from '../../services/data.service';
import { DataUtils } from '../../services/DataUtils';
import { ChartDataFilters } from '../region-comparison-chart/region-comparison-chart.component';
import { countries } from './../../../../countries';
import { continents } from './../../../../regions';
import { subnational } from './../../../../subnational';
import { Category } from '../../models/category.enum';
import { CategoryService } from '../../services/categories.service';

@Component({
  selector: 'mpro-region-comparison',
  templateUrl: './region-comparison.component.html',
  styleUrls: ['./region-comparison.component.scss'],
})
export class RegionComparisonComponent implements OnInit, OnDestroy {
  public isCategoriesEnabled: boolean;
  public selectedCode: string = '';
  public selectedName: string = '';
  @Input() public isFromSizeOfPrize: boolean = false;
  public selectedCountriesRegionName: string = 'Selected Region';
  public tableData: TableRegion[] = [];
  public countries: Map<string, string> = new Map();
  private sort: Sort = new Sort('spending_ppp', SortDirection.DESC);
  public currentlySelectedCountries: TableRegion[] = [];
  public chartData: GroupedBarChartDataModel[] = [];
  public chartDataFilters: ChartDataFilters;
  public dataFilters: DataFilters = new DataFilters();
  public isLoading: boolean = false;

  private dataCacheByYearContinents: GroupedResponseTwoLevels<SimpleStatisticsResponse> =
    new GroupedResponseTwoLevels<SimpleStatisticsResponse>();
  private dataCacheByYearCountries: GroupedResponseTwoLevels<SimpleStatisticsResponse> =
    new GroupedResponseTwoLevels<SimpleStatisticsResponse>();
  private worldDataCacheByYear: GroupedResponse<SimpleStatisticsResponse> =
    new GroupedResponse<SimpleStatisticsResponse>();
  private ngUnsubscribe: Subject<void> = new Subject();
  public currentYear: number = new Date().getFullYear();

  public currency: number = this.dataFilters.currency;

  constructor(
    public dataService: DataService,
    private authService: AuthService,
    private authResourcesService: AuthorizedResourcesService,
    private dataFiltersService: DataFiltersService,
    private cdr: ChangeDetectorRef,
    private categoryService: CategoryService
  ) {
    countries.forEach((c) => this.countries.set(c.code, c.name));
  }

  public ngOnInit(): void {
    this.authService.categoriesEnabled$.subscribe((isCategoriesEnabled) => {
      this.isCategoriesEnabled = isCategoriesEnabled;
    });

    combineLatest([
      this.dataFiltersService.getDataFilters(),
      this.dataService.getData(),
    ])
      .pipe(takeUntil(this.ngUnsubscribe))
      .pipe(tap(() => (this.isLoading = true)))
      .subscribe(([filters, data]) => {
        this.dataFilters = filters;
        if (
          data.countryData.current &&
          data.worldData.current &&
          Object.keys(data.countryData.current).length > 0
        ) {
          this.dataCacheByYearCountries = data.countryData.current;
          if (this.authService.isLoggedIn()) {
            this.dataCacheByYearContinents = this.calculateContinentData(
              data.countryData.current
            );
          }
          this.worldDataCacheByYear = data.worldData.current;
          this.calculateTableData();
          this.calculateChartData();
        }
        this.isLoading = false;
      });
  }

  public ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  public sortChanged(s: Sort) {
    this.sort = s;
  }

  public updateChartDataFilters(chartDataFilters: ChartDataFilters): void {
    this.chartDataFilters = chartDataFilters;
    this.calculateChartData();
  }

  public calculateChartData(): void {
    // todo: reduce "if" complexity
    if (this.chartDataFilters) {
      this.chartData = this.currentlySelectedCountries.map((country) => {
        return {
          group: country.name,
          bars: this.chartDataFilters.projections.map((year) => {
            const theWorld = this.getWorld(year);
            let tableRegion: TableRegion;
            if (country.code === 'WORLD') {
              tableRegion = theWorld;
            } else {
              tableRegion = this.getCountryTableRegion(
                this.currentModeData,
                year,
                country.code
              );
              if (!Object.keys(tableRegion).length) {
                let regionCodes: string[];

                if (this.dataFilters.regionSelection.subnational.size > 1) {
                  regionCodes =
                    this.dataFilters.regionSelection.getSubnationalCodes();
                } else {
                  regionCodes =
                    this.dataFilters.regionSelection.getCountryCodes();
                }

                tableRegion = this.getCustomRegion(
                  year,
                  country.name,
                  country.code,
                  regionCodes
                );
              }
            }

            tableRegion.spending_share =
              (tableRegion.spending_ppp / theWorld.spending_ppp) * 100;

            return {
              key: year.toString(),
              value: tableRegion[this.chartDataFilters.dataLens.value],
              tooltip: this.getBarTooltip(year, tableRegion),
            };
          }),
        };
      });
    }
  }

  public updateCurrentlySelectedCountries(countries: TableRegion[]): void {
    this.currentlySelectedCountries = countries;
    this.cdr.detectChanges();

    this.calculateChartData();
  }

  private getBarTooltip(
    year: number,
    tableRegion: TableRegion
  ): TooltipOptions {
    const chartDescription =
      this.chartDataFilters.dataLens.value === 'spending_ppp'
        ? `${
            this.chartDataFilters.dataLens.name
          } for ${this.dataFilters.spendingGroup.compactDisplayValue()}`
        : this.chartDataFilters.dataLens.name;
    const chartText = Utils.formatNumber(
      tableRegion[this.chartDataFilters.dataLens.value],
      false,
      this.chartDataFilters.dataLens.value === 'people' ? '.0-0' : undefined
    );
    return {
      type: 'chart',
      title: `${tableRegion.name} in ${year}`,
      chartDescription: chartDescription,
      chartText: chartText,
      chartCaption: '',
    };
  }

  private calculateTableData() {
    this.setTableData();
    this.setSelected();

    if (this.sort) {
      if (typeof this.tableData[0][this.sort.property] === 'string') {
        this.tableData.sort((c1, c2) =>
          (c1[this.sort.property] as string).localeCompare(
            c2[this.sort.property] as string
          )
        );
      } else {
        this.tableData.sort(
          (c1, c2) =>
            (c1[this.sort.property] as number) -
            (c2[this.sort.property] as number)
        );
      }

      if (this.sort.direction === SortDirection.DESC) {
        this.tableData.reverse();
      }
    }
  }

  public getCountryTableRegion(
    data: GroupedResponseTwoLevels<SimpleStatisticsResponse>,
    year: number,
    countryCode: string
  ): TableRegion {
    const countryData = data[year][countryCode];
    const countryDataStartYear =
      data[this.authResourcesService.startYear][countryCode];
    const countryDataEndYear =
      data[this.authResourcesService.endYear][countryCode];
    const countryDataCurrentYear = data[this.currentYear][countryCode];
    const tableRegion = new TableRegion();

    if (countryData) {
      tableRegion.code = countryCode;
      tableRegion.name =
        this.countries.get(countryCode) ||
        regionsMap.get(countryCode)?.name ||
        '';
      if (!tableRegion.name && this.dataFilters.dataLens) {
        tableRegion.name =
          subnational[this.dataFilters.regionSelection.getCountryCodes()[0]][
            countryCode
          ];
      }
      this.currency = this.dataFilters.currency;
      tableRegion.people = countryData.headCount;
      tableRegion.spending_ppp = this.dataFilters.spendingPer
        ? countryData.spending2021ppp
        : countryData.spending2021ppp / Utils.getDaysInAYear(year);
      tableRegion.spending_share = 100;
      tableRegion.growthUntilSelected = Utils.annualAverageGrowthRate(
        this.dataFilters.spendingPer
          ? countryDataStartYear.spending2021ppp
          : countryDataStartYear.spending2021ppp /
              Utils.getDaysInAYear(constants.startYear),
        tableRegion.spending_ppp,
        year - this.authResourcesService.startYear
      );
      tableRegion.growthToEndYear = Utils.annualAverageGrowthRate(
        tableRegion.spending_ppp,
        this.dataFilters.spendingPer
          ? countryDataEndYear.spending2021ppp
          : countryDataEndYear.spending2021ppp /
              Utils.getDaysInAYear(this.authResourcesService.endYear),
        this.authResourcesService.endYear - year
      );

      if (this.dataFilters.category && this.dataFilters.category !== '0') {
        tableRegion.spending_ppp = this.dataFilters.spendingPer
          ? this.categoryService.getCategoryData(countryData)
          : this.categoryService.getCategoryData(countryData) /
            Utils.getDaysInAYear(year);
        tableRegion.growthUntilSelected = Utils.annualAverageGrowthRate(
          this.dataFilters.spendingPer
            ? this.categoryService.getCategoryData(countryDataStartYear)
            : this.categoryService.getCategoryData(countryDataStartYear) /
                Utils.getDaysInAYear(year),
          tableRegion.spending_ppp,
          year - constants.currentYear
        );
        tableRegion.growthToEndYear = Utils.annualAverageGrowthRate(
          tableRegion.spending_ppp,
          this.dataFilters.spendingPer
            ? this.categoryService.getCategoryData(countryDataEndYear)
            : this.categoryService.getCategoryData(countryDataEndYear) /
                Utils.getDaysInAYear(this.authResourcesService.endYear),
          this.authResourcesService.endYear - year
        );
      } else {
        tableRegion.spending_ppp = this.dataFilters.currency
          ? this.dataFilters.spendingPer
            ? countryData.spending2021ppp
            : countryData.spending2021ppp / Utils.getDaysInAYear(year)
          : this.dataFilters.spendingPer
          ? countryData.spendingCurrentUsd
          : countryData.spendingCurrentUsd / Utils.getDaysInAYear(year);

        tableRegion.growthUntilSelected = Utils.annualAverageGrowthRate(
          this.dataFilters.currency
            ? this.dataFilters.spendingPer
              ? countryDataStartYear.spending2021ppp
              : countryDataStartYear.spending2021ppp /
                Utils.getDaysInAYear(constants.currentYear)
            : this.dataFilters.spendingPer
            ? countryDataStartYear.spendingCurrentUsd
            : countryDataStartYear.spendingCurrentUsd /
              Utils.getDaysInAYear(constants.currentYear),
          tableRegion.spending_ppp,
          year - constants.currentYear
        );
        tableRegion.growthToEndYear = Utils.annualAverageGrowthRate(
          tableRegion.spending_ppp,
          this.dataFilters.currency
            ? this.dataFilters.spendingPer
              ? countryDataEndYear.spending2021ppp
              : countryDataEndYear.spending2021ppp /
                Utils.getDaysInAYear(constants.currentYear)
            : this.dataFilters.spendingPer
            ? countryDataEndYear.spendingCurrentUsd
            : countryDataEndYear.spendingCurrentUsd /
              Utils.getDaysInAYear(this.authResourcesService.endYear),
          this.authResourcesService.endYear - year
        );
      }
    }

    return tableRegion;
  }

  private get isRegionMode(): boolean {
    return !!(
      this.authService.isLoggedIn() &&
      (this.dataFilters.regionSelection.regions.size ||
        this.dataFilters.regionSelection.countries.size > 1)
    );
  }

  private get currentModeData(): GroupedResponseTwoLevels<SimpleStatisticsResponse> {
    if (this.isRegionMode) {
      return this.dataCacheByYearContinents;
    }

    return this.dataCacheByYearCountries;
  }

  private setTableData() {
    const theWorld = this.getWorld(this.dataFilters.year);
    const worldData = this.worldDataCacheByYear[this.dataFilters.year];

    theWorld.people = worldData.headCount;

    if (this.dataFilters.category && this.dataFilters.category !== '0') {
      theWorld.spending_ppp = this.categoryService.getCategoryData(worldData);
    } else {
      theWorld.spending_ppp = this.dataFilters.currency
        ? this.dataFilters.spendingPer
          ? worldData.spending2021ppp
          : worldData.spending2021ppp /
            Utils.getDaysInAYear(this.dataFilters.year)
        : this.dataFilters.spendingPer
        ? worldData.spendingCurrentUsd
        : worldData.spendingCurrentUsd /
          Utils.getDaysInAYear(this.dataFilters.year);
    }

    this.tableData = [];
    Object.keys(this.currentModeData[this.dataFilters.year]).forEach(
      (areaCode) => {
        const countryTableRegion = this.getCountryTableRegion(
          this.currentModeData,
          this.dataFilters.year,
          areaCode
        );
        // add only if it's allowed
        this.tableData.push(countryTableRegion);
      }
    );

    if (this.dataFilters.regionSelection.subnational.size > 1) {
      const unknownRegion = this.getCustomRegion(
        this.dataFilters.year,
        this.selectedCountriesRegionName,
        this.selectedCountriesRegionName,
        this.dataFilters.regionSelection.getSubnationalCodes()
      );
      this.tableData.push(unknownRegion);
      this.selectedCode = unknownRegion.code;
    }
    const isCustomRegion =
      this.dataFilters.regionSelection.regions.size === 1 &&
      !this.authResourcesService.regions.find(
        (continent) =>
          continent.name ===
          [...this.dataFilters.regionSelection.regions.values()][0].name
      );

    if (
      isCustomRegion ||
      this.dataFilters.regionSelection.regions.size > 1 ||
      (this.dataFilters.regionSelection.regions.size === 0 &&
        this.dataFilters.regionSelection.countries.size > 1)
    ) {
      const unknownRegionName =
        (isCustomRegion &&
          [...this.dataFilters.regionSelection.regions.values()][0].name) ||
        this.selectedCountriesRegionName;
      const unknownRegion = this.getCustomRegion(
        this.dataFilters.year,
        unknownRegionName,
        unknownRegionName,
        this.dataFilters.regionSelection.getCountryCodes()
      );
      this.tableData.push(unknownRegion);
      this.selectedCode = unknownRegion.code;
    }

    this.tableData.forEach(
      (c) => (c.spending_share = (c.spending_ppp / theWorld.spending_ppp) * 100)
    );
    !this.isFromSizeOfPrize && this.tableData.push(theWorld);
  }

  private getWorld(year: number): TableRegion {
    let theWorldName = DataUtils.getWorldName(
      this.dataFilters,
      'The World',
      true
    );

    const theWorld = this.getEmptyTableRegion(theWorldName, 'WORLD');
    const worldData = this.worldDataCacheByYear[year];
    const worldDataStartYear =
      this.worldDataCacheByYear[this.authResourcesService.startYear];
    const worldDataEndYear =
      this.worldDataCacheByYear[this.authResourcesService.endYear];

    theWorld.people = worldData.headCount;

    if (this.dataFilters.category && this.dataFilters.category !== '0') {
      theWorld.spending_ppp = this.dataFilters.spendingPer
        ? this.categoryService.getCategoryData(worldData)
        : this.categoryService.getCategoryData(worldData) /
          Utils.getDaysInAYear(year);

      theWorld.growthUntilSelected = Utils.annualAverageGrowthRate(
        this.dataFilters.spendingPer
          ? this.categoryService.getCategoryData(worldDataStartYear)
          : this.categoryService.getCategoryData(worldDataStartYear) /
              Utils.getDaysInAYear(year),
        theWorld.spending_ppp,
        year - this.authResourcesService.startYear
      );
      theWorld.growthToEndYear = Utils.annualAverageGrowthRate(
        theWorld.spending_ppp,
        this.dataFilters.spendingPer
          ? this.categoryService.getCategoryData(worldDataEndYear)
          : this.categoryService.getCategoryData(worldDataStartYear) /
              Utils.getDaysInAYear(year),
        this.authResourcesService.endYear - year
      );
    } else {
      theWorld.spending_ppp = this.dataFilters.currency
        ? this.dataFilters.spendingPer
          ? worldData.spending2021ppp
          : worldData.spending2021ppp / Utils.getDaysInAYear(year)
        : this.dataFilters.spendingPer
        ? worldData.spendingCurrentUsd
        : worldData.spendingCurrentUsd / Utils.getDaysInAYear(year);

      theWorld.growthUntilSelected = Utils.annualAverageGrowthRate(
        this.dataFilters.currency
          ? this.dataFilters.spendingPer
            ? worldDataStartYear.spending2021ppp
            : worldDataStartYear.spending2021ppp / Utils.getDaysInAYear(year)
          : this.dataFilters.spendingPer
          ? worldDataStartYear.spendingCurrentUsd
          : worldDataStartYear.spendingCurrentUsd / Utils.getDaysInAYear(year),
        theWorld.spending_ppp,
        year - this.authResourcesService.startYear
      );

      theWorld.growthToEndYear = Utils.annualAverageGrowthRate(
        theWorld.spending_ppp,
        this.dataFilters.currency
          ? this.dataFilters.spendingPer
            ? worldDataEndYear.spending2021ppp
            : worldDataEndYear.spending2021ppp / Utils.getDaysInAYear(year)
          : this.dataFilters.spendingPer
          ? worldDataEndYear.spendingCurrentUsd
          : worldDataEndYear.spendingCurrentUsd / Utils.getDaysInAYear(year),
        this.authResourcesService.endYear - year
      );
    }

    return theWorld;
  }

  private setSelected(): void {
    const currentSelection = this.dataFilters.regionSelection;
    if (
      (!currentSelection.regions.size && !currentSelection.countries.size) ||
      (this.dataFilters.dataLens && !currentSelection.subnational.size)
    ) {
      this.selectedCode = 'WORLD';
      this.selectedName = 'The World';
    } else if (currentSelection.regions.size === 1) {
      this.selectedCode = [...currentSelection.regions.values()][0].code;
      this.selectedName = [...currentSelection.regions.values()][0].name;
    } else if (
      !this.dataFilters.dataLens &&
      currentSelection.countries.size === 1
    ) {
      this.selectedCode = [...currentSelection.countries.values()][0].code;
      this.selectedName = [...currentSelection.countries.values()][0].name;
    } else if (currentSelection.subnational.size === 1) {
      this.selectedCode = [...currentSelection.subnational.values()][0].code;
      this.selectedName = [...currentSelection.subnational.values()][0].name;
    } else {
      this.selectedCode = this.selectedCountriesRegionName;
      this.selectedName = this.selectedCode;
    }
  }

  private getCustomRegion(
    year: number,
    regionName: string,
    regionCode: string,
    regionCodes: string[]
  ): TableRegion {
    const continent = this.getEmptyTableRegion(regionName, regionCode);

    regionCodes.forEach((countryCode) => {
      const countryData = this.getCountryTableRegion(
        this.dataCacheByYearCountries,
        year,
        countryCode
      );
      if (countryData.code) {
        continent.spending_ppp += countryData.spending_ppp;
        continent.people += countryData.people;
        continent.growthUntilSelected += countryData.growthUntilSelected;
        continent.growthToEndYear += countryData.growthToEndYear;
      }
    });

    continent.growthUntilSelected =
      continent.growthUntilSelected / regionCodes.length;
    continent.growthToEndYear = continent.growthToEndYear / regionCodes.length;

    return continent;
  }

  private getEmptyTableRegion(
    regionName: string,
    regionCode?: string
  ): TableRegion {
    const tableRegion = new TableRegion();
    tableRegion.code = regionCode || regionName;
    tableRegion.name = regionName;
    tableRegion.people = 0;
    tableRegion.spending_ppp = 0;
    tableRegion.spending_share = 100;
    tableRegion.growthUntilSelected = 0;
    tableRegion.growthToEndYear = 0;

    return tableRegion;
  }

  private calculateContinentData(
    countryData: GroupedResponseTwoLevels<SimpleStatisticsResponse>
  ): GroupedResponseTwoLevels<SimpleStatisticsResponse> {
    return Object.keys(countryData).reduce((groupedResponse, year) => {
      groupedResponse = {
        ...groupedResponse,
        [year]: continents.reduce((response, continent) => {
          response = {
            ...response,
            [continent.code]: continent.countries.reduce(
              (response, countryCode) => {
                const currentCountry = countryData[year][countryCode];
                if (currentCountry) {
                  response.headCount += currentCountry.headCount;

                  response.spending2021ppp += currentCountry.spending2021ppp;
                  response.spendingCurrentLcu +=
                    currentCountry.spendingCurrentLcu;
                  response.spendingCurrentUsd +=
                    currentCountry.spendingCurrentUsd;

                  response.spendingAllPersonalCare +=
                    currentCountry.spendingAllPersonalCare;
                  response.spendingHygiene += currentCountry.spendingHygiene;
                  response.spendingOtherPersonalCare +=
                    currentCountry.spendingOtherPersonalCare;
                  response.spendingBeauty += currentCountry.spendingBeauty;
                  response.spendingMakeup += currentCountry.spendingMakeup;
                  response.spendingHairCare += currentCountry.spendingHairCare;
                  response.spendingSkinCare += currentCountry.spendingSkinCare;
                  response.spendingFragrances +=
                    currentCountry.spendingFragrances;

                  response.spendingPersonalCarePPP +=
                    currentCountry.spendingPersonalCarePPP;
                  response.spendingHygienePPP +=
                    currentCountry.spendingHygienePPP;
                  response.spendingOtherPersonalCarePPP +=
                    currentCountry.spendingOtherPersonalCarePPP;
                  response.spendingBeautyPPP +=
                    currentCountry.spendingBeautyPPP;
                  response.spendingMakeupPPP +=
                    currentCountry.spendingMakeupPPP;
                  response.spendingHairCarePPP +=
                    currentCountry.spendingHairCarePPP;
                  response.spendingSkinCarePPP +=
                    currentCountry.spendingSkinCarePPP;
                  response.spendingFragrancesPPP +=
                    currentCountry.spendingFragrancesPPP;
                }

                return response;
              },
              new SimpleStatisticsResponse()
            ),
          };

          return response;
        }, new GroupedResponse<SimpleStatisticsResponse>()),
      };

      return groupedResponse;
    }, new GroupedResponseTwoLevels<SimpleStatisticsResponse>());
  }
}
